import { SchemaTreeType, SchemaTreeWithPermission } from "../../../types/SchemaTree";
import { GradingService } from "../../../types/GradingService";
import { SchemaWithPermission } from "../../../types/Schema";

export type SchemaTreeGraphEntry = {
  item: SchemaWithPermission | GradingService | undefined;
  level: number;
  /**
   * The **incoming** edge of the schema or grading service.
   * It is undefined for roots of the graph (or subgraph).
   */
  incomingEdge: SchemaTreeWithPermission | undefined;
};

/**
 * The actual schema tree graph. It is a forest graph, consisting of multiple trees.
 * This structure is enforced.
 * Only the schemata need to be in a tree structure, the grading services can be used multiple times.
 * They are always leaves, i.e. never have children.
 * It is **not** enforced that all schemata have children.
 */
export class SchemaTreeGraph {
  // A map from node id to the node itself.
  private readonly schemata: Map<number, SchemaWithPermission>;
  private gradingServices: Map<number, GradingService>;
  private children: Map<
    SchemaWithPermission | undefined,
    Array<{
      target: SchemaWithPermission | GradingService | undefined;
      edge: SchemaTreeWithPermission;
    }>
  >;
  public readonly roots: Set<SchemaWithPermission>;

  /**
   * Creates a new schema tree graph.
   * Empty nodes *and* edges will return an empty graph.
   */
  constructor(nodes: Array<SchemaWithPermission | GradingService>, edges: SchemaTreeWithPermission[]) {
    this.schemata = new Map();
    this.gradingServices = new Map();
    this.children = new Map();

    nodes.forEach((node) => this.addNode(node));
    edges.forEach((edge) => this.addEdge(edge));

    // this.roots as array doesn't work, I have no idea why. The array just gets cleared after the constructor.
    this.roots = this.getRoots();
    this.ensureSchemaForest();
  }

  /**
   * This function adds a new edge to the graph.
   * It fails if the source or target node does not exist.
   * @private
   */
  private addEdge(edge: SchemaTreeWithPermission): void {
    // parent must be a known schema
    let parent = this.schemata.get(edge.parentId);
    // It is (now) possible that the parent is private
    // if (!parent) {
    //   throw new Error(
    //     "SchemaTreeGraph.addEdge: parent is unknown." +
    //       JSON.stringify({
    //         edge: edge,
    //         schemata: this.schemata,
    //         this: this,
    //       }) +
    //       "\n",
    //   );
    // }

    // child must be known
    let child: SchemaWithPermission | GradingService | undefined;
    if (edge.type === SchemaTreeType.GRADING_SERVICE) child = this.gradingServices.get(edge.childId);
    else child = this.schemata.get(edge.childId);

    // It is (now) possible that the parent is private
    // if (!child) {
    //   throw new Error(
    //     "SchemaTreeGraph.addEdge: child is unknown." +
    //       JSON.stringify({
    //         edge: edge,
    //         schemata: this.schemata,
    //         this: this,
    //       }) +
    //       "\n",
    //   );
    // }

    // add empty list if parent not in children
    let childrenList = this.children.get(parent);
    if (!childrenList) this.children.set(parent, [{ target: child, edge: edge }]);
    else childrenList.push({ target: child, edge: edge });
  }

  private addNode(node: SchemaWithPermission | GradingService): void {
    // Test if node has the attribute "topic"
    if ("topic" in node) {
      this.gradingServices.set(node.id, node);
    } else {
      this.schemata.set(node.id, node);
    }
  }

  /**
   *  * Every node must be reachable from EXACTLY one root
   *  * no loops
   *  *  no cycles
   *  * => Same as a standard tree check for a graph with a dummy root that connects to all roots.
   * @private
   */
  private ensureSchemaForest(): void {
    let visited = new Set();
    let stack: Array<SchemaWithPermission> = Array.from(this.roots);
    let maxIterations = this.schemata.size + this.gradingServices.size + 1;

    // prevent infinite loop. If the algorithm is implemented correctly, the loop end should never be reached.
    for (let i = 0; i <= maxIterations; i++) {
      let current = stack.pop();
      if (!current) {
        // found all nodes?
        if (visited.size === this.schemata.size) {
          return;
        } else {
          const totalNodes1: Array<SchemaWithPermission | GradingService> = Array.from(this.schemata.values());
          const totalNodes2: Array<SchemaWithPermission | GradingService> = Array.from(this.gradingServices.values());

          const unreachableNodes = totalNodes1.concat(totalNodes2).filter((node) => !visited.has(node));

          console.error("SchemaTreeGraph: " + unreachableNodes.length + " nodes are unreachable.");
          return;
        }
      }
      if (visited.has(current)) {
        // found a loop or cycle or duplicate way to reach a schema
        throw new Error(
          "SchemaTreeGraph: The following schema has multiple paths to reach it:" + JSON.stringify(current) + "\n",
        );
      }
      visited.add(current);
      const children = this.getDirectChildren(current)
        .map((it) => it.target)
        .flatMap((it) => (it == undefined || "topic" in it ? [] : [it]));
      stack.push(...children);
    }

    throw Error("Implementation error: Tree search is implemented incorrectly.");
  }

  /**
   * Return all schema nodes that have no incoming edge.
   * If there is a grading service node without incoming edge it is **not** returned.
   */
  private getRoots(): Set<SchemaWithPermission> {
    let allSchemata = new Set(Array.from(this.schemata.values()));

    // remove all schemata from the set that have an incoming edge
    Array.from(this.children.values())
      .flat()
      .flatMap((child) => (child.target == undefined || "topic" in child.target ? [] : [child.target]))
      .forEach((child) => allSchemata.delete(child));

    return allSchemata;
  }

  private getDirectChildren(parent: SchemaWithPermission | GradingService | undefined) {
    if (parent === undefined || "topic" in parent) return [];
    return this.children.get(parent) || [];
  }

  // Get a list representation of the tree. The entries are sorted by category (first grading service, then schema) and name.
  // Example (level = indentation):
  // ```
  // |root1, undefinded
  // | gradingservice1, edge1
  // | schema1, edge2
  // |  gradingservice2, edge3
  // |  gradingservice3, edge4
  // |root2, undefined
  // | gradingservice2, edge5
  // ```
  public getOrderedListRepresentation() {
    let res = new Array<SchemaTreeGraphEntry>();

    Array.from(this.roots)
      .sort(this.toOrder)
      .forEach((r) => this.getOrderedChildrenRecursively(r, undefined, 0, res));

    return [...res];
  }

  /**
   * This function returns all successors of a schema. This includes:
   * * the schema with the given id
   * * all direct children of the schema with the given id
   * * recursively all child schemas of the schema with the given id
   *
   * The function returns a list of SchemaTreeEntry objects, which contain the schema or grading service, the level in
   * the subtree starting with the given schema as root (level = 0), and the incoming edge in the schema tree.
   * The given schemas incoming edge is **not** returned, as it is the root of the subtree.
   * The returned successors are in the same order as returned by the getOrderedListRepresentation function.
   *
   * Returns [] if schemaId is not in the graph.
   * @param schemaId
   */
  public getSuccessors(schemaId: number): SchemaTreeGraphEntry[] {
    const schema = this.schemata.get(schemaId);
    if (!schema) return [];

    const res = new Array<SchemaTreeGraphEntry>();
    this.getOrderedChildrenRecursively(schema, undefined, 0, res);
    return res;
  }

  /**
   * Recursively adds all children to tmpRes, ordered by type and name. Level is the depth in the tree.
   * Assumes that the parent is not already in tmpRes.
   */
  private getOrderedChildrenRecursively(
    parent: SchemaWithPermission | GradingService | undefined,
    incomingEdge: SchemaTreeWithPermission | undefined,
    level: number,
    tmpRes: Array<SchemaTreeGraphEntry>,
  ): void {
    tmpRes.push({ item: parent, level: level, incomingEdge: incomingEdge });

    const sortedChildren = this.getDirectChildren(parent)
      .sort((a, b) => this.toOrder(a.target, b.target))
      .map((it) => ({ item: it.target, level: level, incomingEdge: it.edge }));

    sortedChildren.forEach((it) => this.getOrderedChildrenRecursively(it.item, it.incomingEdge, level + 1, tmpRes));
  }

  /**
   * This function is used to sort the nodes in the tree.
   * It first sorts by category (grading service first, then schema, then undefined) and then by name.
   */
  private toOrder(
    node1: SchemaWithPermission | GradingService | undefined,
    node2: SchemaWithPermission | GradingService | undefined,
  ) {
    const toRepresentation = (node: SchemaWithPermission | GradingService | undefined) => {
      if (node === undefined) {
        return "z";
      }
      if ("topic" in node) {
        // grading service
        return "m" + node.name;
      } else {
        // schema
        if (node.writable) return "a" + node.name;
        else return "b" + node.name;
      }
    };

    return toRepresentation(node1).localeCompare(toRepresentation(node2));
  }
}
